import { Cancel, Close, Cloud, InsertDriveFile, Movie, NoteAlt } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { createNewCollectionResource, Resource, ResourceType } from "../../../../models/resource";
import { useSnackbar } from "../../../../components/snackbar";
import { truncateString } from "../../../../utils/truncate-string";
import sanitizeString from "../../../../utils/sanitize-name";
import { useClassroomStore } from "../../../../store/classroom-store";
import { memo, useEffect, useMemo, useState } from "react";
import RichTextEditor from "../../components/rich-text/rich-text-editor";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";

interface EditResourceDialogInterface {
    res: Resource;
    dialogOpen: {
        create: boolean;
        edit: boolean;
    };
    setDialogOpen: React.Dispatch<React.SetStateAction<{
        create: boolean;
        edit: boolean;
    }>>;
    setUpdatingResource: (id: number) => void;
    setUploadPercent: React.Dispatch<React.SetStateAction<number | null>>
}

function EditResourceDialog({ res, dialogOpen, setDialogOpen, setUpdatingResource, setUploadPercent }: EditResourceDialogInterface) {

    const { updateResource } = useClassroomActions();
    
    const { showMessage } = useSnackbar();

    const [resource, setResource] = useState(createNewCollectionResource());


    const memoizedFileObjectUrl = useMemo(() => {
        return resource.file ? URL.createObjectURL(resource.file) : "";
    }, [resource.file]);

    const memoizedresourceUrl = useMemo(() => {
        return `https://d1at3arxf72ma9.cloudfront.net/classrooms/${resource.classroom_id}/resources/${resource.url}`;
    }, [resource.url]);


    const closeDialog = (key: string) => {
        setDialogOpen((prev) => ({ ...prev, [key]: false }));
    };
    

    const handleResourceChange = (field: keyof Resource, value: any) => {
        setResource((prev) => ({
            ...prev,
            [field]: value
        }));
    };

     const _updateResource = async () => {
        closeDialog("edit");
        setUpdatingResource(resource.id);

        if ((resource.type === ResourceType.MEDIA || resource.type === ResourceType.FILE) && resource.file) {
            resource.url = `${resource.collection_id}/${resource.type}/${resource.file?.name}`|| `${sanitizeString(resource.title)}`;
        }
        
        await updateResource(resource.classroom_id, resource.collection_id, resource, (percent) => {
            setUploadPercent(percent);
        });
        setUploadPercent(null);
        setResource(createNewCollectionResource());
        setUpdatingResource(0);
    };

    useEffect(() => {
        setResource(res);
    }, [res]);

    return (
        <Dialog 
            open={dialogOpen.edit} 
            onClose={() => closeDialog("edit")} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                }
            }}
            TransitionProps={{
                timeout: 300,
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg,rgb(83, 147, 196) 0%,rgb(114, 156, 185) 100%)',
                    color: 'white',
                    padding: '20px 28px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #ffffff88, #ffffff44, #ffffff88)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite',
                    },
                    '@keyframes shimmer': {
                    '0%': { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' },
                    },
                }}
            >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                        }}
                    >
                        {resource.type === ResourceType.NOTE && <NoteAlt fontSize="medium" />}
                        {resource.type === ResourceType.MEDIA && <Movie fontSize="medium" />}
                        {resource.type === ResourceType.FILE && <InsertDriveFile fontSize="medium" />}
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                                {resource.type === ResourceType.NOTE && `${truncateString(resource.title, 25)}`}
                            {resource.type === ResourceType.MEDIA && `${truncateString(resource.title, 25)}`}
                            {resource.type === ResourceType.FILE && `${truncateString(resource.title, 25)}`}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={() => closeDialog("edit")}
                    sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                    }}
                >
                    <Close />
                </IconButton>

            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} mt={2}>
                    <TextField
                        label="Title"
                        value={resource.title}
                        onChange={(e) => handleResourceChange('title', e.target.value)}
                        fullWidth
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#f8f9fa',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#f0f1f3',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 0 0 3px rgba(83, 130, 107, 0.1)',
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#53826b',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#53826b',
                                borderWidth: 2,
                            },
                        }}
                    />

                    <RichTextEditor
                        id={String(resource.id)}
                        type="description"
                        value={resource.description}
                        onChange={(val) => handleResourceChange('description', val)}
                    />

                    {(resource.type === ResourceType.MEDIA || resource.type === ResourceType.FILE) && (!resource.file && !resource.url) && (
                        <Box
                            component="label"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                p: 4,
                                border: '2px dashed #e0e4e7',
                                borderRadius: 3,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                backgroundColor: '#fafbfc',
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    borderColor: '#53826b',
                                    backgroundColor: '#f0f8f4',
                                    transform: 'translateY(-2px)',
                                    '& .upload-icon': {
                                        transform: 'scale(1.1)',
                                    },
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(83, 130, 107, 0.1), transparent)',
                                    transition: 'left 0.5s ease',
                                },
                                '&:hover::before': {
                                    left: '100%',
                                },
                            }}
                        >
                            <Box
                                className="upload-icon"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 80,
                                    height: 80,
                                    borderRadius: '20px',
                                    background: 'linear-gradient(135deg, #53826b 0%, #6ba583 100%)',
                                    color: 'white',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 8px 20px rgba(83, 130, 107, 0.3)',
                                }}
                            >
                                <Cloud sx={{ fontSize: 32 }} />
                            </Box>

                            <Box sx={{ textAlign: 'center', zIndex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', mb: 1 }}>
                                    Upload {resource.type === ResourceType.MEDIA ? "Media" : "Document"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Maximum size 200mb
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                    color: '#718096', 
                                    backgroundColor: '#edf2f7', 
                                    padding: '4px 12px', 
                                    borderRadius: '12px',
                                    fontWeight: 500,
                                }}>
                                    {resource.type === ResourceType.MEDIA ? "Images, MP4 videos" : "PDF, DOC, PPT, XLS"}
                                </Typography>
                            </Box>
                            
                            <input
                                hidden
                                type="file"
                                accept={resource.type === ResourceType.MEDIA ? "image/*,video/mp4" : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    const MAX_SIZE_MB = 200;
                                    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
                                    if (file) {
                                        if (file.size > MAX_SIZE_BYTES) {
                                            showMessage("file size exceeds maximum", 0);
                                            e.target.value = "";
                                            return;
                                        }
                                        const sanitized = new File([file], sanitizeString(file.name), { type: file.type });
                                        handleResourceChange("file", sanitized);
                                    }
                                }}
                            />
                        </Box>
                    )}

                        {(resource.type === ResourceType.MEDIA || resource.type === ResourceType.FILE) && (resource.file || resource.url) && (() => {
                        const previewURL = resource.file ? memoizedFileObjectUrl : memoizedresourceUrl;

            
                        
                        const isImage = resource.file ? resource.file.type.startsWith("image/") : /\.(jpg|jpeg|png|gif)$/i.test(resource.url);
                        const isVideo = resource.file ? resource.file.type.startsWith("video/") : /\.mp4$/i.test(resource.url);
                        const isDoc = resource.type === ResourceType.FILE;

                        return (
                            <Box
                                sx={{
                                    position: "relative",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: "1px solid #e2e8f0",
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                    }
                                }}
                            >
                                {isImage && (
                                    <Box sx={{ position: 'relative' }}>
                                        <img
                                            src={previewURL}
                                            alt="Preview"
                                            style={{ 
                                                width: "100%", 
                                                height: "240px",
                                                objectFit: "contain", 
                                                display: "block" 
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                                                padding: '20px 16px 16px',
                                                color: 'white',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 500,  }}>
                                                {resource.title}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {isVideo && (
                                    <Box sx={{ position: 'relative' }}>
                                        <video
                                            src={previewURL}
                                            controls
                                            style={{ 
                                                width: "100%", 
                                                height: "240px",
                                                objectFit: "contain", 
                                                display: "block" 
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                left: 12,
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            VIDEO
                                        </Box>
                                    </Box>
                                )}

                                {isDoc && (
                                    <Box sx={{ 
                                        padding: 3, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2,
                                        backgroundColor: '#f8f9fa',
                                    }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                backgroundColor: '#53826b',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '20px',
                                            }}
                                        >
                                            📄
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {resource.title}
                                            </Typography>
                                
                                        </Box>
                                    </Box>
                                )}

                                <IconButton
                                    onClick={() => {
                                        if (resource.file) {
                                            URL.revokeObjectURL(previewURL);
                                            handleResourceChange("file", null);
                                        } else {
                                            handleResourceChange("removed_resource", resource.url);
                                            handleResourceChange("url", '');
                                        }
                                    }}
                                    sx={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        backgroundColor: "rgba(239, 68, 68, 0.9)",
                                        color: "#fff",
                                        width: 36,
                                        height: 36,
                                        backdropFilter: 'blur(10px)',
                                        "&:hover": {
                                            backgroundColor: "rgba(220, 38, 38, 0.95)",
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <Cancel sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                        );
                    })()}
                </Stack>
            </DialogContent>
            
            <DialogActions sx={{ 
                padding: '20px 28px', 
                borderTop: '1px solid #f1f3f4',
                backgroundColor: '#fafbfc',
                gap: 2,
            }}>
                <Button
                    onClick={_updateResource}
                    variant="contained"
                    disabled={resource.type === ResourceType.NOTE ? !resource.title : !resource.title || (!resource.url && !resource.file)}
                    sx={{ 
                        background: 'linear-gradient(135deg, #53826b 0%, #6ba583 100%)',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '10px 24px',
                        boxShadow: '0 4px 12px rgba(83, 130, 107, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #4a7460 0%, #5f9474 100%)',
                            boxShadow: '0 6px 16px rgba(83, 130, 107, 0.4)',
                            transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                            background: '#e5e7eb',
                            color: '#9ca3af',
                            boxShadow: 'none',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    Update {resource.type === ResourceType.NOTE ? "Note" : resource.type === ResourceType.MEDIA ? "Media" : "Document"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditResourceDialog;